/**
 * forecastModel — Linear regression + prediction intervals for month-end forecasting.
 *
 * Uses simple-statistics for regression fitting, then manually computes
 * prediction interval bands (high/medium/low) for projected cumulative sales.
 *
 * The model works on cumulative MTD data:
 *   - X-axis = business day index (1, 2, 3, ...)
 *   - Y-axis = cumulative sales total at that day
 *
 * Open orders are deliberately NOT part of the forecast — see buildForecast.
 */

import {
  linearRegression,
  linearRegressionLine,
  mean,
} from "simple-statistics";
import { DailyTrendPoint, PeriodMetrics } from "./PowerBIApiCall";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ForecastDataPoint {
  /** Business day index (1-based) */
  dayIndex: number;
  /** Label for X-axis, e.g. "Day 5" or date string */
  label: string;
  /** Actual cumulative sales (null for future days) */
  actual: number | null;
  /** Medium forecast — regression trend (null for past days) */
  forecast: number | null;
  /** Upper prediction band (null for past days) */
  forecastHigh: number | null;
  /** Lower prediction band (null for past days) */
  forecastLow: number | null;
  /** Monthly budget (Full) — horizontal reference */
  budget: number;
}

export interface ForecastSummary {
  /** Projected month-end total (medium line) */
  forecastTotal: number;
  /** Upper bound month-end total */
  forecastHigh: number;
  /** Lower bound month-end total */
  forecastLow: number;
  /** R-squared goodness of fit (0-1) */
  rSquared: number;
  /** How many actual data points used */
  dataPointsUsed: number;
  /** Regression-implied avg daily sales rate (for extending to QTD/YTD forecasts) */
  impliedDailyRate: number;
}

export interface ForecastResult {
  data: ForecastDataPoint[];
  summary: ForecastSummary;
}

// ─── Internal helpers ───────────────────────────────────────────────────────

type DataPair = [number, number];

interface RegressionModel {
  predict: (x: number) => number;
  slope: number;
  intercept: number;
  see: number; // standard error of the estimate
  n: number;
  xMean: number;
  ssX: number; // sum of squared deviations of x
}

/**
 * Fit a linear regression and compute all statistics needed for prediction intervals.
 */
function fitRegression(data: DataPair[]): RegressionModel {
  const reg = linearRegression(data);
  const line = linearRegressionLine(reg);
  const n = data.length;

  const xValues = data.map((d) => d[0]);
  const xMean = mean(xValues);

  // Sum of squared deviations of x from its mean
  const ssX = xValues.reduce((sum, x) => sum + (x - xMean) ** 2, 0);

  // Sum of squared residuals
  const ssr = data.reduce((sum, [x, y]) => {
    const residual = y - line(x);
    return sum + residual ** 2;
  }, 0);

  // Standard Error of the Estimate (degrees of freedom = n - 2)
  const see = n > 2 ? Math.sqrt(ssr / (n - 2)) : 0;

  return { predict: line, slope: reg.m, intercept: reg.b, see, n, xMean, ssX };
}

/**
 * Approximate t-critical value for common confidence levels.
 * For n >= 10, this is close enough for visual prediction bands.
 */
function tCritical(n: number, confidence: number = 0.8): number {
  const df = n - 2;
  if (confidence >= 0.95) {
    if (df <= 5) return 2.571;
    if (df <= 10) return 2.228;
    if (df <= 20) return 2.086;
    return 1.96;
  }
  // Default 80% — gives a tighter, more readable band
  if (df <= 5) return 1.476;
  if (df <= 10) return 1.372;
  if (df <= 20) return 1.325;
  return 1.282;
}

/**
 * Calculate prediction interval half-width at a given x value.
 */
function predictionMargin(
  model: RegressionModel,
  x: number,
  confidence: number,
): number {
  const t = tCritical(model.n, confidence);
  const sePred =
    model.see *
    Math.sqrt(
      1 + 1 / model.n + (x - model.xMean) ** 2 / Math.max(model.ssX, 1),
    );
  return t * sePred;
}

/**
 * Filter out outlier data pairs using the IQR method on the y-values.
 * Keeps the x-index intact so the regression correctly models the time axis.
 */
function filterOutlierPairs(pairs: DataPair[], k: number = 2.0): DataPair[] {
  const yValues = pairs.map((p) => p[1]);
  const sorted = [...yValues].sort((a, b) => a - b);
  const q1 = sorted[Math.floor(sorted.length * 0.25)];
  const q3 = sorted[Math.floor(sorted.length * 0.75)];
  const iqr = q3 - q1;
  const lo = q1 - k * iqr;
  const hi = q3 + k * iqr;
  return pairs.filter(([, y]) => y >= lo && y <= hi);
}

/**
 * Minimum band half-width as a fraction of the cumulative forecast.
 * Ensures prediction bands are always visually meaningful, even when
 * the regression has high n (many data points) and tight SEE.
 * 10% provides a ±10% range — e.g., for a $2.5M forecast, low ≈ $2.25M, high ≈ $2.75M.
 */
const MIN_BAND_PCT = 0.1;

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Build a cumulative MTD forecast with high/medium/low prediction bands.
 *
 * @param dailyTrend     Rolling daily sales data (may span multiple months)
 * @param mtdMetrics     MTD period metrics (for business days, open orders)
 * @param budgetFull     Full monthly budget (for the reference line)
 * @param opts.confidence Prediction interval confidence (default 0.95)
 */
export function buildForecast(
  dailyTrend: DailyTrendPoint[],
  mtdMetrics: PeriodMetrics,
  budgetFull: number,
  opts: { confidence?: number } = {},
): ForecastResult | null {
  const confidence = opts.confidence ?? 0.95;
  if (!dailyTrend || dailyTrend.length < 2) return null;

  const totalBusinessDays = mtdMetrics.businessDaysTotal;
  const elapsedDays = mtdMetrics.businessDaysElapsed;

  if (totalBusinessDays <= 0 || elapsedDays <= 0) return null;

  // ── Step 1: Split data into regression set and current-month set ─────
  // The dailyTrend array has rolling data (may span 60+ days).
  // We use ALL available days for regression fitting (stronger trend signal),
  // but only show current-month days on the chart X-axis.
  const allDailyValues = dailyTrend.map((d) => d.dailySales);

  // Current-month days: the last `elapsedDays` entries (or fewer if data is short)
  const monthDays = dailyTrend.slice(-Math.min(elapsedDays, dailyTrend.length));

  if (monthDays.length < 2) return null;

  // ── Step 2: Build cumulative actual series (current month only) ──────
  // Use the authoritative MTD actual from Power BI (mtdMetrics.actual) rather
  // than summing daily values, which may include outlier adjustments that
  // distort the running total. We back-compute the cumulative series so that
  // the final day aligns exactly with the known MTD actual.
  const monthDailyValues = monthDays.map((d) => d.dailySales);
  const rawCumulative: number[] = [];
  let runningTotal = 0;
  for (const v of monthDailyValues) {
    runningTotal += v;
    rawCumulative.push(runningTotal);
  }

  // Scale the cumulative series so the last value matches the known MTD actual.
  // This corrects for any month-end adjustments / outliers in the daily data.
  const rawTotal = rawCumulative[rawCumulative.length - 1];
  const mtdActual = mtdMetrics.actual;
  const scaleFactor = rawTotal !== 0 ? mtdActual / rawTotal : 1;
  const cumulativeActual = rawCumulative.map((v) =>
    Math.round(v * scaleFactor),
  );

  // ── Step 3: Fit regression on business days only ──────────────────────
  // The rolling data includes weekends/holidays with $0 sales.
  // We MUST exclude these from the regression — otherwise the predicted
  // daily value is diluted by ~30% zeros, drastically underforecasting.
  // Since we project forward for `remainingDays` (business days only),
  // the regression should reflect business-day-level sales.
  //
  // Filter: keep days with positive sales (business days), then re-index
  // sequentially so the regression models the business-day trend.
  const businessDayValues = allDailyValues.filter((v) => v > 0);

  // Also filter out extreme outliers (e.g., month-end adjustments) using IQR
  const businessDayPairs: DataPair[] = businessDayValues.map((v, i) => [i, v]);
  const filteredPairs = filterOutlierPairs(businessDayPairs);
  const regressionPairs =
    filteredPairs.length >= 3 ? filteredPairs : businessDayPairs;
  const model = fitRegression(regressionPairs);

  // R-squared: how well the line fits (computed on filtered business-day data)
  const regressionY = regressionPairs.map((p) => p[1]);
  const yMean = mean(regressionY);
  const ssTotal = regressionY.reduce((s, v) => s + (v - yMean) ** 2, 0);
  const ssResidual = regressionPairs.reduce((s, [x, y]) => {
    const predicted = model.predict(x);
    return s + (y - predicted) ** 2;
  }, 0);
  const rSquared = ssTotal > 0 ? 1 - ssResidual / ssTotal : 0;

  // ── Step 4: Build chart data ─────────────────────────────────────────
  const chartData: ForecastDataPoint[] = [];
  const actualDayCount = monthDays.length;
  const remainingDays = totalBusinessDays - elapsedDays;
  // The regression is indexed on sequential business days (0..n-1) over the
  // WHOLE window, and filterOutlierPairs drops pairs while keeping those
  // original indices. So the next business day is businessDayValues.length —
  // NOT regressionPairs.length, which would restart the projection one day
  // earlier per dropped outlier and shift the rate by slope x dropped.
  const regressionNextIndex = businessDayValues.length;

  // Past days (actuals)
  for (let i = 0; i < actualDayCount; i++) {
    chartData.push({
      dayIndex: i + 1,
      label: monthDays[i].dateFormatted || `Day ${i + 1}`,
      actual: cumulativeActual[i],
      forecast: null,
      forecastHigh: null,
      forecastLow: null,
      budget: budgetFull,
    });
  }

  // ── Step 5: Project forward with prediction bands ────────────────────
  // The last actual cumulative total is our starting point.
  // Throughput only. Open orders are NOT added: the order book is the input to
  // the same shipping pipeline the historical daily rate already measures, so
  // adding it on top double-counts (it was 62% of one portco's remaining-month
  // uplift). Open orders are reported on their own instead.
  const lastActual = cumulativeActual[cumulativeActual.length - 1];
  const forecastBaseline = lastActual;

  // Bridge point: connect actual line to forecast line
  // Only set forecast values if there are remaining days to project
  if (chartData.length > 0 && remainingDays > 0) {
    const bridgePoint = chartData[chartData.length - 1];
    bridgePoint.forecast = forecastBaseline;
    bridgePoint.forecastHigh = forecastBaseline;
    bridgePoint.forecastLow = forecastBaseline;
  }

  // Cumulative sums for medium/high/low from the forecast start
  let cumMedium = forecastBaseline;
  let cumHigh = forecastBaseline;
  let cumLow = forecastBaseline;

  for (let i = 0; i < remainingDays; i++) {
    // Continue the business-day regression index from where the data ended.
    // Each projected day is a business day, matching the regression's index space.
    const regressionDayIndex = regressionNextIndex + i;
    const predictedDaily = Math.max(0, model.predict(regressionDayIndex));
    const margin = predictionMargin(model, regressionDayIndex, confidence);

    const dailyHigh = Math.max(0, predictedDaily + margin);
    const dailyLow = Math.max(0, predictedDaily - margin);

    cumMedium += predictedDaily;
    cumHigh += dailyHigh;
    cumLow += dailyLow;

    // Enforce minimum band width: at least ±MIN_BAND_PCT of the medium forecast.
    // With high n (60 days) and stable data, the statistical prediction interval
    // can be artificially narrow — this floor ensures visually meaningful bands.
    const minHalfWidth = cumMedium * MIN_BAND_PCT;
    if (cumHigh - cumMedium < minHalfWidth) {
      cumHigh = cumMedium + minHalfWidth;
    }
    if (cumMedium - cumLow < minHalfWidth) {
      cumLow = cumMedium - minHalfWidth;
    }

    chartData.push({
      dayIndex: elapsedDays + i + 1,
      label: `Day ${elapsedDays + i + 1}`,
      actual: null,
      forecast: Math.round(cumMedium),
      forecastHigh: Math.round(cumHigh),
      forecastLow: Math.round(Math.max(0, cumLow)),
      budget: budgetFull,
    });
  }

  // ── Step 6: Build summary ────────────────────────────────────────────
  // Read the running totals, not the last chart point. On the final business
  // day of the month remainingDays is 0, so no forecast point is ever pushed
  // and the last point is an actual with forecast === null — falling back to
  // lastActual there silently dropped open orders from the forecast (and
  // disagreed with the server copy, which already reads cumMedium).
  const forecastTotal = Math.round(cumMedium);
  // Implied daily rate: the average daily sales the regression projects
  // (forecastTotal - actual) / remaining business days
  const impliedDailyRate =
    remainingDays > 0 ? (forecastTotal - lastActual) / remainingDays : 0;
  const summary: ForecastSummary = {
    forecastTotal,
    forecastHigh: Math.round(cumHigh),
    forecastLow: Math.round(Math.max(0, cumLow)),
    rSquared: Math.max(0, rSquared),
    dataPointsUsed: regressionPairs.length,
    impliedDailyRate: Math.max(0, impliedDailyRate),
  };

  return { data: chartData, summary };
}

// ─── Longer-period forecasts (QTD / YTD) ────────────────────────────────────

/**
 * How the period is tracking against its own plan, as a ratio (1.0 = on plan).
 *
 * Defaults to 1.0 when there is no budget to measure against, so a missing
 * budget forecasts to plan rather than to zero.
 */
export function attainmentRate(period: PeriodMetrics): number {
  return period.budgetProrated > 0 ? period.actual / period.budgetProrated : 1;
}

/**
 * Forecast a period that runs past the current month (QTD / YTD).
 *
 * Splits the remaining time into two pieces, because we have different
 * information about each:
 *
 *   1. Rest of the CURRENT month — we have daily actuals, so use the
 *      regression month-end forecast. Best available signal.
 *   2. FUTURE months — no actuals exist, so take each month's own budget and
 *      scale it by recent attainment. `budgetFull - budgetProrated` carries
 *      the budget's seasonal shape for free, because `[YTD Budget (Prorated)]`
 *      counts completed months in full and prorates only the current one.
 *
 * This replaces holding one month's daily rate flat to year end, which ignored
 * seasonality entirely while being measured against a seasonalized budget —
 * it read a peak September rate as if Oct-Dec were September.
 *
 * `attainment` is the caller's choice of base; callers pass YTD.
 *
 * QTD was tried first and is a trap: it assumes the current quarter is the
 * stable middle ground between MTD and YTD, which fails exactly when one
 * quarter is the anomaly. Vega ran 94.5% of plan in H1 and 142.6% in Q3, so a
 * QTD base projected the hot quarter across Q4 and forecast the full year
 * +17.0% vs budget while the year to date was only +8.8% — off a future-month
 * budget of $19.1M, i.e. ~19x leverage on the multiplier, with no bound.
 * YTD is the widest stable base and keeps forecast-vs-budget near
 * actual-vs-budget. Note this cannot distinguish a genuine step-change from a
 * pull-forward; the base encodes which you assume.
 */
export function buildPeriodForecast(
  period: PeriodMetrics,
  mtd: PeriodMetrics,
  monthEndForecast: number,
  attainment: number,
): number {
  // Budget still to come in this period, and the slice of it that is the
  // remainder of the current month (which the regression already covers).
  const remainingPeriodBudget = Math.max(
    0,
    period.budgetFull - period.budgetProrated,
  );
  const restOfMonthBudget = Math.max(0, mtd.budgetFull - mtd.budgetProrated);
  const futureMonthsBudget = Math.max(
    0,
    remainingPeriodBudget - restOfMonthBudget,
  );

  const restOfMonth = Math.max(0, monthEndForecast - mtd.actual);

  return period.actual + restOfMonth + futureMonthsBudget * attainment;
}
